import React from 'react';
import {
  LayoutDashboard,
  ArrowUpDown,
  Coins,
  PieChart,
  Settings,
  History,
  TrendingUp,
  Shield,
  X,
  Bot,
  Trophy,
  Users,
  Crown,
  Target,
  Gift,
  Zap,
  Lock,
  Building2,
  Flame,
  KeyRound,
  Fish,
  Wallet,
  DollarSign,
  Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../ui/utils';
import { ActiveTab } from './MainLayout';

interface SidebarProps {
  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'portfolio', label: 'Portfolio', icon: PieChart },
  { id: 'swap', label: 'Swap', icon: ArrowUpDown },
  { id: 'staking', label: 'Staking', icon: Coins },
  { id: 'trading-bots', label: 'Trading Bots', icon: Zap },
  { id: 'ai-trading', label: 'AI Trading', icon: Bot },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const quickActions = [
  { id: 'transactions', label: 'Transaction History', icon: History },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  { id: 'security', label: 'Security', icon: Shield },
];

const exclusiveFeatures = [
  { id: 'gamification', label: 'Achievement Hub', icon: Trophy },
  { id: 'social-trading', label: 'Social Trading', icon: Users },
  { id: 'ndh-exclusive', label: 'Nardium Exclusive', icon: Crown },
  { id: 'predictions', label: 'Prediction Arena', icon: Target },
  { id: 'referrals', label: 'Referral Program', icon: Gift },
];

const smartFeatures = [
  { id: 'dca', label: 'DCA Inteligente', icon: DollarSign, badge: 'NEW' },
  { id: 'whale-tracking', label: 'Whale Tracking', icon: Fish, badge: 'HOT' },
  { id: 'multi-wallet', label: 'Multi-Wallet', icon: Wallet, badge: null },
  { id: 'yield-boost', label: 'Yield Boost', icon: Sparkles, badge: null },
];

export function Sidebar({ activeTab, setActiveTab, isOpen, onToggle }: SidebarProps) {
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div className={cn(
        "fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-[#0f0f0f] border-r border-slate-200 dark:border-[#262626] transform transition-transform lg:transform-none",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="flex flex-col h-full">
          {/* Mobile close button */}
          <div className="flex items-center justify-between p-4 lg:hidden">
            <span className="text-lg font-semibold">Menu</span>
            <Button variant="ghost" size="sm" onClick={onToggle}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Main Menu
              </h3>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                
                return (
                  <Button
                    key={item.id}
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start",
                      isActive && "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    )}
                    onClick={() => {
                      setActiveTab(item.id as ActiveTab);
                      if (window.innerWidth < 1024) {
                        onToggle();
                      }
                    }}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.label}
                  </Button>
                );
              })}
            </div>

            <div>
              <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Quick Actions
              </h3>
              {quickActions.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <Button
                    key={item.label}
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start",
                      isActive && "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    )}
                    onClick={() => {
                      setActiveTab(item.id as ActiveTab);
                      if (window.innerWidth < 1024) {
                        onToggle();
                      }
                    }}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.label}
                  </Button>
                );
              })}
            </div>

            <div>
              <h3 className="text-xs font-semibold text-purple-500 dark:text-purple-400 uppercase tracking-wider mb-3">
                Nardium Exclusive 💎
              </h3>
              {exclusiveFeatures.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <Button
                    key={item.label}
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start",
                      isActive && "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                    )}
                    onClick={() => {
                      setActiveTab(item.id as ActiveTab);
                      if (window.innerWidth < 1024) {
                        onToggle();
                      }
                    }}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.label}
                  </Button>
                );
              })}
            </div>

            <div>
              <h3 className="text-xs font-semibold text-green-500 dark:text-green-400 uppercase tracking-wider mb-3">
                Smart Features 🚀
              </h3>
              {smartFeatures.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <Button
                    key={item.label}
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start",
                      isActive && "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                    )}
                    onClick={() => {
                      setActiveTab(item.id as ActiveTab);
                      if (window.innerWidth < 1024) {
                        onToggle();
                      }
                    }}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.label}
                    {item.badge && (
                      <span className={cn(
                        "ml-auto text-xs px-1.5 py-0.5 rounded-full",
                        item.badge === 'NEW' && "bg-green-500/20 text-green-400",
                        item.badge === 'HOT' && "bg-orange-500/20 text-orange-400"
                      )}>
                        {item.badge}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
          </nav>

          {/* Botones Especiales - NARI, INFRA VAULT & INFRABANK */}
          <div className="px-4 pb-4 space-y-3">
            {/* NARI Guardian Dragon Button */}
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start relative overflow-hidden group",
                "bg-gradient-to-r from-yellow-600/20 to-orange-600/20 hover:from-yellow-600/30 hover:to-orange-600/30",
                "border border-yellow-500/50 hover:border-yellow-400",
                "transition-all duration-300",
                activeTab === 'nari' && "ring-2 ring-yellow-500/50"
              )}
              onClick={() => {
                setActiveTab('nari' as ActiveTab);
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <Flame className="mr-3 h-5 w-5 text-yellow-400" />
              <span className="font-semibold text-yellow-300">Nari</span>
              <div className="ml-auto">
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/30 text-yellow-200">🐉</span>
              </div>
            </Button>

            {/* INFRA VAULT Button */}
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start relative overflow-hidden group",
                "bg-gradient-to-r from-violet-600/20 to-purple-600/20 hover:from-violet-600/30 hover:to-purple-600/30",
                "border border-violet-500/50 hover:border-violet-400",
                "transition-all duration-300"
              )}
              onClick={() => {
                setActiveTab('infra-vault' as ActiveTab);
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <KeyRound className="mr-3 h-5 w-5 text-violet-400" />
              <span className="font-semibold text-violet-300">INFRA VAULT</span>
              <div className="ml-auto">
                <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/30 text-violet-200">🔒</span>
              </div>
            </Button>

            {/* INFRABANK Button */}
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start relative overflow-hidden group",
                "bg-gradient-to-r from-cyan-600/20 to-blue-600/20 hover:from-cyan-600/30 hover:to-blue-600/30",
                "border border-cyan-500/50 hover:border-cyan-400",
                "transition-all duration-300"
              )}
              onClick={() => {
                setActiveTab('infrabank' as ActiveTab);
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <Building2 className="mr-3 h-5 w-5 text-cyan-400" />
              <span className="font-semibold text-cyan-300">INFRABANK</span>
              <div className="ml-auto">
                <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/30 text-cyan-200">🏦</span>
              </div>
            </Button>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-200 dark:border-[#262626]">
            <div className="text-xs text-slate-500 dark:text-slate-400 text-center">
              Nardium v1.0.0
            </div>
          </div>
        </div>
      </div>
    </>
  );
}